import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { useT } from '../lib/i18n.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTwinName } from '../lib/twin.js';
import { useDevice } from '../lib/device.js';

// Desktop "at a glance" rail — persistent daily numbers in the space the sidebar
// frees up. Reuses the digest query (deduped with the mobile banner).
function DigestRail() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data } = useQuery<{ digest: Digest }>({
    queryKey: ['admin', 'digest'], queryFn: () => api('/api/admin/digest'),
    enabled: isOwner, staleTime: 5 * 60_000,
  });
  if (!isOwner) return null;
  const d = data?.digest;
  const rows: Array<{ label: string; value: number }> = d ? [
    { label: 'Réactions auto', value: d.autoToday },
    { label: 'À réviser (Vault)', value: d.pendingReview },
    { label: 'À aligner', value: d.needsAlignment },
    { label: 'Partagées à confirmer', value: d.sharedPending },
    { label: 'Questions en file', value: d.questionsQueued },
  ] : [];
  return (
    <aside className="sticky top-6 rounded-2xl border border-ink/10 bg-white/[0.02] p-4 space-y-3">
      <div className="text-[11px] text-particle/80">Aujourd’hui en bref</div>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-3">
            <span className="font-serif text-[13px] text-ink/60">{r.label}</span>
            <span className={`font-serif text-lg ${r.value > 0 ? 'text-ink' : 'text-ink/30'}`}>{r.value}</span>
          </li>
        ))}
      </ul>
      <div className="pt-2 border-t border-ink/10 flex items-center justify-between">
        <span className="font-serif text-[13px] text-ink/60">Dépensé</span>
        <span className="font-serif text-lg text-ink">{d ? `$${d.spendTodayUsd.toFixed(2)}` : '—'}</span>
      </div>
    </aside>
  );
}

interface Digest {
  date: string; autoToday: number; pendingReview: number; memoriesToday: number;
  needsAlignment: number; sharedPending: number; questionsQueued: number; spendTodayUsd: number;
}

// One-glance daily digest for the owner — steer the autonomous pipeline in 10s.
function DigestBanner() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [dismissed, setDismissed] = useState(false);
  const { data } = useQuery<{ digest: Digest }>({
    queryKey: ['admin', 'digest'],
    queryFn: () => api('/api/admin/digest'),
    enabled: isOwner,
    staleTime: 5 * 60_000,
  });
  const d = data?.digest;
  if (!isOwner || !d || dismissed) return null;
  const dismissKey = `mdai_digest_dismissed_${d.date}`;
  if (typeof localStorage !== 'undefined' && localStorage.getItem(dismissKey)) return null;
  const interesting = d.autoToday > 0 || d.pendingReview > 0 || d.needsAlignment > 0 || d.sharedPending > 0;
  if (!interesting) return null;
  return (
    <div className="w-full mb-4 px-4 py-2.5 rounded-xl border border-particle/25 bg-particle/[0.04] flex items-center gap-3 flex-wrap">
      <span className="text-[11px] text-particle/80">digest</span>
      <span className="font-serif text-[13px] text-ink/75 flex-1">
        {d.autoToday > 0 && <>le twin a généré <b>{d.autoToday}</b> réaction{d.autoToday > 1 ? 's' : ''} aujourd’hui · </>}
        {d.pendingReview > 0 && <><b>{d.pendingReview}</b> à réviser (Vault ✨) · </>}
        {d.needsAlignment > 0 && <><b>{d.needsAlignment}</b> à aligner · </>}
        {d.sharedPending > 0 && <><b>{d.sharedPending}</b> partagées à confirmer · </>}
        {d.spendTodayUsd > 0 ? <>~{d.spendTodayUsd.toFixed(2)} $ dépensés</> : <>0 $ dépensé</>}
      </span>
      <button onClick={() => { localStorage.setItem(dismissKey, '1'); setDismissed(true); }}
        className="text-ink/40 hover:text-ink text-[12px] px-1">✕</button>
    </div>
  );
}

// A news-sourced stance the twin formed, awaiting the owner's quick verdict.
interface Stance {
  id: string;
  source: string;
  context: string | null;
  question: string;
  statement: string;
  reasoning: string | null;
  confidence: number | null;
  predicted: boolean | null;
  newsUrl?: string | null;
  newsHeadline?: string | null;
}

// A fetched news item offered as a suggested full read.
interface NewsItem { id: string; headline: string; url: string | null; source: string; topics: string[] }

// Today = a quick daily alignment on the day's news. The twin finds a story and
// states what it thinks Martin would feel; Martin agrees/disagrees in one tap
// (optional comment). Deeper work lives in Align; this is the fast daily pass.
export function Today() {
  const { isMobile } = useDevice();
  const qc = useQueryClient();
  const twinName = useTwinName();
  const [comment, setComment] = useState('');
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ analyses: Stance[] }>({
    queryKey: ['analysis', 'pending'],
    queryFn: () => api('/api/analysis?filter=pending'),
  });

  const queue = (data?.analyses ?? []).filter((a) => a.source === 'news' && !skipped.has(a.id));
  const current = queue[0];

  // Suggested full reads — the news the twin found. Confirming one refines the
  // profile (folds its topics into interests) and is your daily reading.
  const newsQ = useQuery<{ items: NewsItem[] }>({ queryKey: ['news', 'items'], queryFn: () => api('/api/news/items') });
  const [readDismissed, setReadDismissed] = useState<Set<string>>(new Set());
  const reads = (newsQ.data?.items ?? []).filter((n) => !readDismissed.has(n.id)).slice(0, 12);
  const interested = useMutation({
    mutationFn: (id: string) => api<{ added: string[] }>(`/api/news/items/${id}/interested`, { method: 'POST' }),
    onSuccess: (r, id) => {
      setReadDismissed((s) => new Set(s).add(id));
      setMsg(r.added.length ? `Ajouté à tes intérêts : ${r.added.join(', ')}` : 'Noté ✓');
      qc.invalidateQueries({ queryKey: ['news', 'interests'] });
    },
  });

  const verdict = useMutation({
    mutationFn: (v: { id: string; verdict: boolean }) =>
      api(`/api/analysis/${v.id}/verdict`, { method: 'POST', body: JSON.stringify({ verdict: v.verdict, comment: comment.trim() || null }) }),
    // Await the refetch so the next stance shows immediately (no manual refresh).
    onSuccess: async () => { setComment(''); await qc.refetchQueries({ queryKey: ['analysis', 'pending'] }); },
  });

  async function findNews() {
    setRunning(true); setMsg(null);
    try {
      const r = await api<{ generated: number; stopped: string | null }>(
        '/api/analysis/run', { method: 'POST', body: JSON.stringify({ language: 'fr', count: 5 }) });
      // Force an immediate refetch so freshly generated stances appear without a
      // manual page refresh.
      await qc.refetchQueries({ queryKey: ['analysis', 'pending'] });
      if (r.generated === 0) {
        setMsg(r.stopped === 'no_news' ? 'Aucune actualité fraîche — ajoute des flux dans Réglages → Twin → News.'
          : r.stopped === 'daily_cap_reached' ? 'Plafond quotidien atteint.'
          : 'Aucune générée pour le moment.');
      }
    } catch {
      setMsg('Mode API requis (Réglages → Engine → LLM) pour aller chercher l’actualité.');
    } finally { setRunning(false); }
  }

  return (
    <div className="px-4 md:px-8 pb-12 max-w-5xl mx-auto">
      {isMobile && <DigestBanner />}
      <div className="md:grid md:grid-cols-[1fr_260px] md:gap-8 md:items-start">
        <div className="min-w-0 max-w-2xl">
      <header className="pt-2 mb-6">
        <h1 className="font-serif text-3xl text-ink">Aujourd’hui</h1>
        <p className="font-serif italic text-ink/55 mt-1">
          Ton alignement express sur l’actu — {twinName} donne son avis, tu confirmes ou corriges.
        </p>
      </header>

      {isLoading ? (
        <div className="py-20 text-center text-[12px] text-ink/40 animate-pulse">…</div>
      ) : !current ? (
        <div className="py-16 text-center space-y-4">
          <p className="font-serif text-2xl text-ink">C’est ton check du jour. ✓</p>
          <p className="font-serif italic text-ink/55">Reviens demain — ou va chercher d’autres actualités maintenant.</p>
          <button onClick={findNews} disabled={running}
            className="px-5 py-2.5 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-40">
            {running ? 'Recherche…' : 'Trouver l’actualité'}
          </button>
          {msg && <p className="font-serif italic text-[13px] text-ink/55 max-w-md mx-auto">{msg}</p>}
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-ink/12 bg-white/[0.02] p-5 md:p-6 space-y-4">
            {/* The story */}
            <div>
              <div className="text-[11px] text-particle/80 mb-1.5">Actu du jour</div>
              <p className="font-serif text-lg text-ink/90 leading-snug">{current.newsHeadline || current.context || current.question}</p>
              {current.newsUrl && (
                <a href={current.newsUrl} target="_blank" rel="noreferrer"
                  className="inline-block mt-1 text-[11.5px] text-sky-300/80 hover:text-sky-300">lire l’article ↗</a>
              )}
            </div>

            {/* The twin's interpretation */}
            <div className="border-t border-ink/10 pt-4">
              <div className="text-[11px] text-ink/40 mb-1.5">
                L’avis de {twinName}{current.confidence != null ? ` · ${current.confidence}%` : ''}
              </div>
              <p className="font-serif text-xl text-ink leading-snug">{current.statement}</p>
              {current.reasoning && <p className="font-serif italic text-[14px] text-ink/55 mt-2 leading-relaxed">“{current.reasoning}”</p>}
            </div>

            {/* Quick verdict + optional comment */}
            <div className="border-t border-ink/10 pt-4 space-y-3">
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                placeholder="Une nuance ? (optionnel) — « oui mais… »"
                className="w-full px-3 py-2 rounded-xl border border-ink/10 bg-white/[0.02] font-serif text-[15px] text-ink placeholder:italic placeholder:text-ink/35 focus:border-ink/30 focus:outline-none resize-none" />
              <div className="flex items-center gap-2">
                <button onClick={() => verdict.mutate({ id: current.id, verdict: true })} disabled={verdict.isPending}
                  className="flex-1 py-2.5 rounded-full border border-emerald-300/40 text-emerald-300 text-[11px] hover:bg-emerald-300/10 disabled:opacity-40">
                  👍 D’accord
                </button>
                <button onClick={() => verdict.mutate({ id: current.id, verdict: false })} disabled={verdict.isPending}
                  className="flex-1 py-2.5 rounded-full border border-rose-300/40 text-rose-300 text-[11px] hover:bg-rose-300/10 disabled:opacity-40">
                  👎 Pas d’accord
                </button>
                <button onClick={() => { setComment(''); setSkipped((s) => new Set(s).add(current.id)); }} disabled={verdict.isPending}
                  title="Passer pour aujourd’hui"
                  className="px-4 py-2.5 rounded-full border border-ink/15 text-[11px] text-ink/45 hover:text-ink disabled:opacity-40">
                  Passer
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center text-[11.5px] text-ink/35">
            {queue.length} à aligner aujourd’hui
          </div>
        </>
      )}

      {/* Suggested full reads — confirm what interests you (refines your profile). */}
      {reads.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-lg text-ink">Lectures du jour</h2>
          <p className="font-serif italic text-[13px] text-ink/50 mt-0.5 mb-3">
            Confirme celles qui t’intéressent — ça affine ton profil et garde tes lectures du jour.
          </p>
          <ul className="space-y-2">
            {reads.map((n) => (
              <li key={n.id} className="rounded-xl border border-ink/10 bg-white/[0.02] p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[15px] text-ink/90 leading-snug">{n.headline}</div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-ink/35">
                    <span className="truncate">{n.source}</span>
                    {n.url && (
                      <a href={n.url} target="_blank" rel="noreferrer"
                        className="text-sky-300/80 hover:text-sky-300 normal-case tracking-normal">lire ↗</a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => interested.mutate(n.id)} disabled={interested.isPending}
                    title="Ça m’intéresse — ajoute le sujet à mon profil"
                    className="w-8 h-8 grid place-items-center rounded-full border border-emerald-300/40 text-emerald-300 hover:bg-emerald-300/10 disabled:opacity-40">✓</button>
                  <button onClick={() => setReadDismissed((s) => new Set(s).add(n.id))}
                    title="Masquer" className="w-8 h-8 grid place-items-center rounded-full border border-ink/15 text-ink/40 hover:text-ink">✕</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
        </div>

        {!isMobile && <DigestRail />}
      </div>
    </div>
  );
}
